import React from 'react'
import PropTypes from 'prop-types'
import ReactSVG from 'react-svg'
import { Alert } from 'react-bootstrap'

import { LoadingSpinner } from '../../components'
import { client } from '../../utils'
import { dashboard as endpoints } from '../../api_urls'

import DashboardBox from './DashboardBox'
import OverallStatistics from './OverallStatistics'
import AssessmentPerformance from './AssessmentPerformance'
import OverallClassEngagement from './OverallClassEngagement'
import {
  OverallHelp,
  StudentPerfHelp,
  AssessmentPerfHelp,
  EngagementHelp,
  ClassPerfHelp,
} from './DashboardLayoutOverlays'

class DashboardLayout extends React.Component {
  constructor(props) {
    super(props)
    this.updateFlexDirection = this.updateFlexDirection.bind(this)
    this.state = {
      flexDirection: 'row',
      classPerfLoading: true,
      classPerfError: false,
      studPerfLoading: true,
      studPerfError: false,
      overallLoading: true,
      overallError: false,
      overallStatistics: {
        atrisk: 0,
        average: 0,
        excellent: 0,
      },
      engagementLoading: true,
      engagementError: false,
      engagementStatistics: {
        readingKnobMean: 0,
        readingKnobColor: '',
        videosKnobMean: 0,
        videosKnobColor: '',
        assessmentKnobMean: 0,
        assessmentKnobColor: '',
      },
    }
  }

  static propTypes = {
    coursenum: PropTypes.string.isRequired,
    studentCount: PropTypes.string.isRequired,
  }

  componentDidMount() {
    this.fetchOverallStatistics()
    this.fetchEngagementStatistics()
    this.updateFlexDirection()
    window.addEventListener('resize', this.updateFlexDirection)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateFlexDirection)
  }

  async fetchOverallStatistics() {
    let url = endpoints.getOverallStatisticsURL()
    try {
      var response = await client.get(url)
    } catch (err) {
      console.error(`Error fetching ${url}`, err)
      return this.setState({ overallLoading: false, overallError: true })
    }
    this.setState({
      overallStatistics: response.data.overall_statistics,
      overallLoading: false,
    })
  }

  async fetchEngagementStatistics() {
    let url = endpoints.getEngagementStatisticsURL()
    try {
      var response = await client.get(url)
    } catch (err) {
      console.error(`Error fetching ${url}`, err)
      return this.setState({ engagementLoading: false, engagementError: true })
    }
    this.setState({
      engagementStatistics: response.data.engagement_statistics,
      engagementLoading: false,
    })
  }

  updateFlexDirection() {
    let flexDirection = $(window).width() > 768 ? 'row' : 'column'
    this.setState({ flexDirection })
  }

  handleCPLoad = data => {
    if (!(data instanceof SVGElement) && !this.state.classPerfError) {
      this.setState({ classPerfError: true })
    }

    /*
       ReactSVG uses renderToStaticMarkup in it's source code, so whenever ANY
       state changes in a parent component, it must re-render the entire SVG.
       This causes an infinite loop if the callback from rendering the svg
       changes the state. The if statement circumvents that so the svg
       component is only rendered twice.
     */

    if (this.state.classPerfLoading) {
      this.setState({ classPerfLoading: false })
    }
  }

  handleSPLoad = data => {
    if (!(data instanceof SVGElement) && !this.state.studPerfError) {
      this.setState({ studPerfError: true })
    }
    if (this.state.studPerfLoading) {
      this.setState({ studPerfLoading: false })
    }
  }

  getLoadingError = (loading, error) => {
    let html = loading ? (
      <div className="text-center">
        <LoadingSpinner text="Compiling Graph" />
      </div>
    ) : (
      <span />
    )
    html = error ? (
      <Alert bsStyle="warning" className="text-center">
        Content failed to load
      </Alert>
    ) : (
      html
    )
    return html
  }

  render() {
    const width = '100%'
    let cpLoading = this.getLoadingError(
      this.state.classPerfLoading,
      this.state.classPerfError
    )
    let spLoading = this.getLoadingError(
      this.state.studPerfLoading,
      this.state.classPerfError
    )

    return (
      <div>
        <DashboardBox title="Overall Statistics" helpIcon={OverallHelp}>
          <OverallStatistics
            coursenum={this.props.coursenum}
            statistics={this.state.overallStatistics}
            loading={this.state.overallLoading}
            error={this.state.overallError}
            studentCount={this.props.studentCount}
          />
        </DashboardBox>

        <DashboardBox
          title="Student Performance"
          helpIcon={StudentPerfHelp}
          detailLink={`/instructor/comparative/${this.props.coursenum}`}
        >
          <span>{spLoading}</span>
          <ReactSVG
            path={`/instructor/dashboard.student_performance/${this.props.coursenum}/content.svg`}
            onInjected={svg => {
              this.handleSPLoad(svg)
            }}
            svgStyle={{ width: width }}
            className="student-performance-svg"
          />
        </DashboardBox>

        <DashboardBox
          title="Assessment Performance"
          helpIcon={AssessmentPerfHelp}
          detailLink={`/instructor/activities/${this.props.coursenum}`}
          detailText="Go to Assessments"
        >
          <AssessmentPerformance
            coursenum={this.props.coursenum}
            flexDirection={this.state.flexDirection}
          />
        </DashboardBox>

        <div
          style={{ display: 'flex', flexDirection: this.state.flexDirection }}
        >
          <div style={{ flex: 1, marginRight: '20px' }}>
            <DashboardBox
              title="Class Engagement"
              helpIcon={EngagementHelp}
              detailLink={`/instructor/engagement/${this.props.coursenum}`}
            >
              <OverallClassEngagement
                statistics={this.state.engagementStatistics}
                loading={this.state.engagementLoading}
                error={this.state.engagementError}
              />
            </DashboardBox>
          </div>
          <div style={{ flex: 2.2 }}>
            <DashboardBox
              title="Class Performance"
              helpIcon={ClassPerfHelp}
              detailLink={`/course/progress/${this.props.coursenum}`}
            >
              <span>{cpLoading}</span>
              <ReactSVG
                path={`/course/progress.prepare_histogram/${this.props.coursenum}/content.svg`}
                onInjected={svg => {
                  this.handleCPLoad(svg)
                }}
                svgStyle={{ width: width }}
                className="class-performance-svg"
              />
            </DashboardBox>
          </div>
        </div>
      </div>
    )
  }
}

export default DashboardLayout
